import axios from 'axios';
import config from '../config/config';

const api = axios.create({
  baseURL: config.API_URL,
});

api.interceptors.request.use((req) => {
  const token = localStorage.getItem('token');
  if (token) {
    req.headers.Authorization = `Bearer ${token}`;
  }
  return req;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  changePassword: (data) => api.put('/auth/password', data),
};

export const userAPI = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  uploadAvatar: (formData) => api.post('/users/avatar', formData),
};

export const logementAPI = {
  getAll: (params) => api.get('/logements', { params }),
  getById: (id) => api.get(`/logements/${id}`),
  create: (data) => api.post('/logements', data),
  update: (id, data) => api.put(`/logements/${id}`, data),
  delete: (id) => api.delete(`/logements/${id}`),
  getMyProperties: (params) => api.get('/logements/mes-annonces', { params }),
  addFavorite: (propertyId) => api.post(`/logements/${propertyId}/favoris`),
  removeFavorite: (propertyId) => api.delete(`/logements/${propertyId}/favoris`),
  getMyFavorites: () => api.get('/logements/favoris'),
  getFavorites: (studentId) => api.get(`/logements/favoris/${studentId}`),
  uploadImages: (id, formData) => api.post(`/logements/${id}/images`, formData),
  addImageUrl: (id, url) => api.post(`/logements/${id}/images-url`, { url }),
  deleteImage: (id, imageUrl) => api.delete(`/logements/${id}/images`, { data: { imageUrl } }),
};

export const requirementAPI = {
  getByType: (type) => api.get(`/demarches/requirements/${type}`),
  getAll: (params) => api.get('/demarches/requirements', { params }),
  create: (data) => api.post('/demarches/requirements', data),
  update: (id, data) => api.put(`/demarches/requirements/${id}`, data),
  delete: (id) => api.delete(`/demarches/requirements/${id}`),
};

export const demarcheAPI = {
  getAll: () => api.get('/demarches'),
  getAllAdmin: (params) => api.get('/demarches/all', { params }),
  getById: (id) => api.get(`/demarches/${id}`),
  create: (data) => api.post('/demarches', data),
  updateStatus: (id, data) => api.put(`/demarches/${id}/statut`, data),
  uploadDocument: (id, formData) => api.post(`/demarches/${id}/documents`, formData),
  getHistory: (id) => api.get(`/demarches/${id}`),
  getStats: () => api.get('/demarches/stats'),
};

export const guideAPI = {
  getAll: (params) => api.get('/guide', { params }),
  getCategories: () => api.get('/guide/categories'),
  getByCategorie: (categorie, params) => api.get(`/guide/${categorie}`, { params }),
  getById: (id) => api.get(`/guide/detail/${id}`),
  create: (data) => api.post('/guide', data),
  update: (id, data) => api.put(`/guide/${id}`, data),
  delete: (id) => api.delete(`/guide/${id}`),
};

export const messageAPI = {
  getThreads: () => api.get('/messages'),
  getConversation: (otherUserId, params) => api.get(`/messages/${otherUserId}`, { params }),
  send: (data) => api.post('/messages', data),
  markAsRead: (id) => api.put(`/messages/${id}/read`),
};

export const communityAPI = {
  getAll: (params) => api.get('/communautes', { params }),
  getById: (id) => api.get(`/communautes/${id}`),
  create: (data) => api.post('/communautes', data),
  update: (id, data) => api.put(`/communautes/${id}`, data),
  delete: (id) => api.delete(`/communautes/${id}`),
  addComment: (postId, data) => api.post(`/communautes/${postId}/comments`, data),
  deleteComment: (postId, commentId) => api.delete(`/communautes/${postId}/comments/${commentId}`),
};

export const notificationAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/unread'),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllAsRead: () => api.put('/notifications/read-all'),
  delete: (id) => api.delete(`/notifications/${id}`),
};

export default api;
